import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import safeToast from "utils/safeToast";
import { API_ENDPOINTS, getUploadUrl } from "config/api";

// MDUI Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Autocomplete from "@mui/material/Autocomplete";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";

import { decryptText } from "config/encryption";

const EducationDetails = () => {
  const [education, setEducation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [addInstDialogOpen, setAddInstDialogOpen] = useState(false);
  const [editingEducation, setEditingEducation] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState({ Token: "", StudentId: 0, LoginId: 0 });

  const [form, setForm] = useState({
    eduId: 0,
    instType: "",
    district: "",
    inst: "",
    medium: "",
    degree: "",
    txtCourse: "",
    txtFrom: "",
    txtTo: "",
    txtPers: "",
  });

  const [newInstitution, setNewInstitution] = useState({
    fullName: "",
    shortName: "",
    notes: "",
  });

  const [dropdowns, setDropdowns] = useState({
    instTypes: [],
    districts: [],
    degrees: [],
    institutions: [],
    medium: [],
  });

  useEffect(() => {
    fetchEducationDetails();
  }, []);

  useEffect(() => {
    const { instType, district } = form;
    if (instType && district) {
      const url = API_ENDPOINTS.PROFILE.GET_INSTITUTES_BY_TYPE(instType, district);
      axios
        .get(url, { headers: { Authorization: `Bearer ${userInfo.Token}` } })
        .then((res) => {
          const institutions = res.data.map((item) => ({ id: item.id, txt: item.ifn }));
          setDropdowns((prev) => ({ ...prev, institutions }));
          // Only reset inst field, preserve eduId
          setForm((prev) => ({ ...prev, inst: "" }));
        })
        .catch(() => {
          safeToast.error("Failed to load institutions.");
        });
    } else {
      setDropdowns((prev) => ({ ...prev, institutions: [] }));
      // Only reset inst field, preserve eduId
      setForm((prev) => ({ ...prev, inst: "" }));
    }
  }, [form.instType, form.district, userInfo.Token]);

  const fetchEducationDetails = async () => {
    try {
      const encryptedData = localStorage.getItem("UserLogDetails");
      if (!encryptedData) throw new Error("User session not found.");
      const decryptedData = decryptText(encryptedData);
      const parsed = JSON.parse(decryptedData);
      setUserInfo(parsed);

      // Use V2 API endpoint
      const eduRes = await axios.get(API_ENDPOINTS.PROFILE.GET_EDUCATION_DETAILS_V2(parsed.StudentId), {
        headers: { Authorization: `Bearer ${parsed.Token}` },
      });

      setEducation(eduRes.data || []);

      const dropdownRes = await axios.get(API_ENDPOINTS.PROFILE.GET_DROPDOWNS, {
        headers: { Authorization: `Bearer ${parsed.Token}` },
      });

      setDropdowns({
        instTypes: dropdownRes.data.instTypes || [],
        districts: dropdownRes.data.districts || [],
        degrees: dropdownRes.data.degrees || [],
        institutions: [],
        medium:
          dropdownRes.data.languages?.map((item) => ({
            id: item.id,
            txt: item.txt,
          })) || [],
      });
    } catch (error) {
      if (error.response?.status === 401) {
        safeToast.error("Session expired. Please log in again.");
        localStorage.clear();
        navigate("/BasicAuth");
      } else {
        safeToast.error("Failed to load education details.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (edu = null) => {
    if (edu) {
      setEditingEducation(edu);
      // Handle both Id (capital) and id (lowercase) from API
      // Note: ID can be negative (SQL Server IDENTITY with negative seed)
      const eduId = edu.Id !== undefined ? edu.Id : (edu.id !== undefined ? edu.id : 0);
      console.log("Opening edit mode - Education ID:", eduId, "Full edu object:", edu);
      setForm({
        eduId: eduId,
        instType: edu.instTypeId || "",
        district: edu.districtId || "",
        inst: edu.prevInstId || "",
        medium: edu.mediumId || "",
        degree: edu.degreeId || "",
        txtCourse: edu.course || "",
        txtFrom: edu.startDate || "",
        txtTo: edu.endDate || "",
        txtPers: edu.percentage || "",
      });

      if (edu.instTypeId && edu.districtId) {
        const url = API_ENDPOINTS.PROFILE.GET_INSTITUTES_BY_TYPE(edu.instTypeId, edu.districtId);
        axios
          .get(url, { headers: { Authorization: `Bearer ${userInfo.Token}` } })
          .then((res) => {
            const institutions = res.data.map((item) => ({ id: item.id, txt: item.ifn }));
            setDropdowns((prev) => ({ ...prev, institutions }));
          })
          .catch(() => {
            safeToast.error("Failed to load institutions.");
          });
      }
      setActiveStep(0); // Reset to first step for editing
    } else {
      setEditingEducation(null);
      setForm({
        eduId: 0,
        instType: "",
        district: "",
        inst: "",
        medium: "",
        degree: "",
        txtCourse: "",
        txtFrom: "",
        txtTo: "",
        txtPers: "",
      });
      setDropdowns((prev) => ({ ...prev, institutions: [] }));
      setActiveStep(0); // Start from first step
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingEducation(null);
    setActiveStep(0);
  };

  const handleNext = () => {
    // Validate current step before proceeding
    if (activeStep === 0) {
      // Step 1: Institution details
      if (!form.instType || !form.district || !form.inst) {
        safeToast.error("Please fill all required fields in Institution Details");
        return;
      }
    } else if (activeStep === 1) {
      // Step 2: Academic details
      if (!form.degree || !form.medium) {
        safeToast.error("Please fill all required fields in Academic Details");
        return;
      }
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const steps = [
    {
      label: "Institution Details",
      description: "Select institution type, district, and institution",
    },
    {
      label: "Academic Details",
      description: "Choose degree, course, and medium of instruction",
    },
    {
      label: "Duration & Performance",
      description: "Enter duration and academic performance",
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    // Comprehensive validation
    const errors = [];

    if (!form.instType) errors.push("Institution Type");
    if (!form.district) errors.push("District");
    if (!form.inst) errors.push("Institution");
    if (!form.degree) errors.push("Degree");
    if (!form.medium) errors.push("Medium");

    // Validate percentage
    const percentage = parseFloat(form.txtPers) || 0;
    if (percentage < 0 || percentage > 100) {
      errors.push("Percentage must be between 0 and 100");
    }

    // Validate date format (YYYY-MM)
    if (form.txtFrom && !/^\d{4}-\d{2}$/.test(form.txtFrom)) {
      errors.push("Start date must be in format YYYY-MM");
    }
    if (form.txtTo && !/^\d{4}-\d{2}$/.test(form.txtTo)) {
      errors.push("End date must be in format YYYY-MM");
    }

    // Validate date logic
    if (form.txtFrom && form.txtTo) {
      const fromYear = parseInt(form.txtFrom.split("-")[0]);
      const toYear = parseInt(form.txtTo.split("-")[0]);
      if (toYear < fromYear) {
        errors.push("End date cannot be before start date");
      }
    }

    if (errors.length > 0) {
      safeToast.error(`Validation errors: ${errors.join(", ")}`);
      return;
    }

    // Ensure we have the correct ID for updates - check multiple sources
    // Priority: form.eduId (set when opening edit) > editingEducation object
    // Note: ID can be negative (SQL Server IDENTITY with negative seed), so check !== 0 instead of > 0
    let educationId = 0;
    if (form.eduId !== undefined && form.eduId !== null && form.eduId !== 0) {
      educationId = parseInt(form.eduId);
    } else if (editingEducation) {
      const eduIdFromObj = editingEducation.Id !== undefined ? editingEducation.Id : editingEducation.id;
      if (eduIdFromObj !== undefined && eduIdFromObj !== null && eduIdFromObj !== 0) {
        educationId = parseInt(eduIdFromObj);
      }
    }
    
    console.log("Submitting - editingEducation:", editingEducation, "form.eduId:", form.eduId, "final educationId:", educationId);
    
    // Final check: if we're in edit mode but don't have an ID, log warning
    if (editingEducation && educationId === 0) {
      console.warn("WARNING: Editing education but ID is 0. Education object:", editingEducation);
    }
    
    const payload = {
      id: educationId !== 0 ? educationId : null,
      studentId: userInfo.StudentId,
      instTypeId: parseInt(form.instType),
      districtId: parseInt(form.district),
      institutionId: parseInt(form.inst),
      mediumId: parseInt(form.medium),
      degreeId: parseInt(form.degree),
      course: form.txtCourse || null,
      fromMonYr: form.txtFrom || null,
      toMonYr: form.txtTo || null,
      percentage: percentage,
      remarks: null,
      createdUserId: userInfo.StudentId,
      loginId: userInfo.LoginId || 0,
    };

    try {
      let res;
      if (educationId !== 0 && educationId !== null) {
        // Update
        res = await axios.put(API_ENDPOINTS.PROFILE.UPDATE_EDUCATION_DETAIL_V2, payload, {
          headers: { Authorization: `Bearer ${userInfo.Token}` },
        });
      } else {
        // Insert
        res = await axios.post(API_ENDPOINTS.PROFILE.INSERT_EDUCATION_DETAIL_V2, payload, {
          headers: { Authorization: `Bearer ${userInfo.Token}` },
        });
      }

      const message = res.data?.message || res.data || "";
      
      if (message.includes("Error:") || message.includes("not found") || message.includes("already exists")) {
        safeToast.error(message);
      } else {
        safeToast.success(message);
        handleClose();
        fetchEducationDetails();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        safeToast.error("Session expired. Please log in again.");
        localStorage.clear();
        navigate("/BasicAuth");
      } else {
        const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || "Failed to save education info.";
        safeToast.error(errorMsg);
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this education record?")) {
      return;
    }

    try {
      const res = await axios.delete(API_ENDPOINTS.PROFILE.DELETE_EDUCATION_DETAIL_V2(id, userInfo.StudentId), {
        headers: { Authorization: `Bearer ${userInfo.Token}` },
      });

      const message = res.data?.message || res.data || "";
      
      if (message.includes("Error:") || message.includes("not found")) {
        safeToast.error(message);
      } else {
        safeToast.success(message);
        fetchEducationDetails();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        safeToast.error("Session expired. Please log in again.");
        localStorage.clear();
        navigate("/BasicAuth");
      } else {
        const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || "Failed to delete education info.";
        safeToast.error(errorMsg);
      }
    }
  };

  const handleAddInstitution = async () => {
    if (!form.instType || !form.district) {
      safeToast.error("Please select Institution Type and District first.");
      return;
    }

    const payload = {
      instTypeId: parseInt(form.instType),
      districtId: parseInt(form.district),
      fullName: newInstitution.fullName,
      shortName: newInstitution.shortName,
      notes: newInstitution.notes,
      createdUserId: userInfo.StudentId,
    };

    try {
      const res = await axios.post(API_ENDPOINTS.PROFILE.INSERT_INSTITUTE, payload, {
        headers: { Authorization: `Bearer ${userInfo.Token}` },
      });

      safeToast.success("Institution added successfully!");

      const url = API_ENDPOINTS.PROFILE.GET_INSTITUTES_BY_TYPE(form.instType, form.district);
      const refreshed = await axios.get(url, {
        headers: { Authorization: `Bearer ${userInfo.Token}` },
      });

      const updatedInstitutions = refreshed.data.map((i) => ({ id: i.id, txt: i.ifn }));
      setDropdowns((prev) => ({ ...prev, institutions: updatedInstitutions }));
      setForm((prev) => ({
        ...prev,
        inst: res.data.id,
      }));
      setAddInstDialogOpen(false);
      setNewInstitution({ fullName: "", shortName: "", notes: "" });
    } catch (error) {
      safeToast.error("Failed to add institution.");
    }
  };

  const formatDateRange = (from, to) => {
    if (!from && !to) return "Date not specified";
    if (!from) return `Until ${to}`;
    if (!to) return `Since ${from}`;
    return `${from} - ${to}`;
  };

  if (loading) {
    return (
      <MDBox display="flex" justifyContent="center" py={3}>
        <CircularProgress />
      </MDBox>
    );
  }

  return (
    <MDBox py={2} px={3}>
      <Card sx={{ boxShadow: 2, borderRadius: 2, bgcolor: "background.paper" }}>
        <MDBox
          p={2}
          mb={1}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ borderBottom: "1px solid", borderColor: "divider" }}
        >
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ color: "text.secondary" }}>school</Icon>
            <MDTypography variant="h6" color="text" fontWeight="medium">
              Education
            </MDTypography>
            {education.length > 0 && (
              <Chip
                label={education.length}
                size="small"
                color="primary"
                sx={{
                  fontWeight: "medium",
                  height: "24px",
                }}
              />
            )}
          </MDBox>
          <Tooltip title="Add Education">
            <MDButton
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => handleOpen()}
              startIcon={<Icon>add</Icon>}
            >
              Add
            </MDButton>
          </Tooltip>
        </MDBox>

        <CardContent sx={{ p: 3, bgcolor: "background.paper" }}>
          {education.length === 0 ? (
            <MDBox textAlign="center" py={6}>
              <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>school</Icon>
              <MDTypography variant="h6" color="text" fontWeight="medium" mb={1}>
                No Education Records
              </MDTypography>
             
              <MDButton variant="outlined" color="primary" onClick={() => handleOpen()}>
                Add Your First Education
              </MDButton>
            </MDBox>
          ) : (
            <Grid container spacing={2}>
              {education.map((edu) => (
                <Grid item xs={12} key={edu.id}>
                  <Card sx={{ boxShadow: 1, borderRadius: 2, bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}>
                    <CardContent sx={{ p: 2, bgcolor: "background.paper" }}>
                      <MDBox display="flex" justifyContent="flex-end" alignItems="center" mb={1}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpen(edu)}
                            sx={{ color: "primary.main" }}
                          >
                            <Icon fontSize="small">edit</Icon>
                          </IconButton>
                        </Tooltip>
                      </MDBox>
                      <MDBox mb={1.5}>
                        <MDTypography variant="caption" color="success.main" mb={0.5}>
                          {edu.institutionName || "Institution Name"}
                        </MDTypography>
                        <MDTypography variant="body2" color="text" mb={0.5}>
                          {edu.degreeName || "Degree"}
                        </MDTypography>
                        {edu.course && (
                          <MDTypography variant="body2" color="text" mb={1}>
                            {edu.course}
                          </MDTypography>
                        )}
                      </MDBox>
                      <Divider sx={{ my: 1 }} />
                      <MDBox>
                        <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                          <Icon sx={{ fontSize: "1rem", color: "text.secondary" }}>category</Icon>
                          <MDTypography variant="caption" color="text.secondary">
                            Institution Type:
                          </MDTypography>
                          <MDTypography variant="caption" color="text" fontWeight="medium">
                            {edu.instTypeName}
                          </MDTypography>
                        </MDBox>
                        {edu.mediumName && (
                          <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                            <Icon sx={{ fontSize: "1rem", color: "text.secondary" }}>language</Icon>
                            <MDTypography variant="caption" color="text.secondary">
                              Medium:
                            </MDTypography>
                            <MDTypography variant="caption" color="text" fontWeight="medium">
                              {edu.mediumName}
                            </MDTypography>
                          </MDBox>
                        )}
                        {edu.percentage > 0 && (
                          <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                            <Icon sx={{ fontSize: "1rem", color: "success.main" }}>star</Icon>
                            <MDTypography variant="caption" color="text.secondary">
                              Percentage:
                            </MDTypography>
                            <MDTypography variant="caption" fontWeight="bold" color="success.main">
                              {edu.percentage}%
                            </MDTypography>
                          </MDBox>
                        )}
                        <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                          <Icon sx={{ fontSize: "1rem", color: "text.secondary" }}>calendar_today</Icon>
                          <MDTypography variant="caption" color="text.secondary">
                            Duration:
                          </MDTypography>
                          <MDTypography variant="caption" color="text" fontWeight="medium">
                            {formatDateRange(edu.startDate, edu.endDate)}
                          </MDTypography>
                        </MDBox>
                        <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                          <Icon sx={{ fontSize: "1rem", color: "text.secondary" }}>location_on</Icon>
                          <MDTypography variant="caption" color="text.secondary">
                            District:
                          </MDTypography>
                          <MDTypography variant="caption" color="text" fontWeight="medium">
                            {edu.districtName || "N/A"}
                          </MDTypography>
                        </MDBox>
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <Icon sx={{ fontSize: "1rem", color: "text.secondary" }}>check_circle</Icon>
                          <MDTypography variant="caption" color="text.secondary">
                            Status:
                          </MDTypography>
                          <Chip
                            label={edu.status || "Active"}
                            size="small"
                            color={edu.isDeleted ? "error" : "success"}
                            sx={{ height: "20px", fontSize: "0.7rem" }}
                          />
                        </MDBox>
                      </MDBox>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Education Modal */}
      <Dialog 
        open={open} 
        onClose={handleClose} 
        fullWidth 
        maxWidth="md"
        scroll="paper"
        PaperProps={{ 
          sx: { 
            borderRadius: 2,
            maxHeight: "90vh",
            bgcolor: "background.paper",
          } 
        }}
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6" fontWeight="medium">
              {editingEducation ? "Edit Education" : "Add Education"}
            </MDTypography>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{ color: "text.secondary" }}
            >
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent sx={{ maxHeight: "75vh", overflowY: "auto", p: 3, bgcolor: "background.paper" }}>
          <Stepper 
            activeStep={activeStep} 
            orientation="vertical"
            sx={{
              bgcolor: "background.paper !important",
              background: "none !important",
              boxShadow: "none !important",
              padding: 0,
              "& .MuiStep-root": {
                bgcolor: "background.paper",
              },
              "& .MuiStepLabel-root": {
                bgcolor: "background.paper",
                "& .MuiStepLabel-label": {
                  color: "text.primary",
                  "&.Mui-active": {
                    color: "primary.main",
                  },
                  "&.Mui-completed": {
                    color: "text.primary",
                  },
                },
              },
              "& .MuiStepContent-root": {
                bgcolor: "background.paper",
                borderLeft: "2px solid",
                borderColor: "divider",
                marginLeft: "20px",
                paddingLeft: "24px",
              },
              "& .MuiStepConnector-root": {
                marginLeft: "20px",
                "& .MuiStepConnector-line": {
                  borderColor: "divider",
                  borderWidth: "2px",
                },
                "&.Mui-active .MuiStepConnector-line": {
                  borderColor: "divider",
                },
                "&.Mui-completed .MuiStepConnector-line": {
                  borderColor: "divider",
                },
              },
              "& .MuiStepIcon-root": {
                color: "divider",
                border: "2px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                "&.Mui-active": {
                  color: "primary.main",
                  borderColor: "primary.main",
                  bgcolor: "background.paper",
                },
                "&.Mui-completed": {
                  color: "primary.main",
                  borderColor: "primary.main",
                  bgcolor: "background.paper",
                },
              },
            }}
          >
            {/* Step 1: Institution Details */}
            <Step>
              <StepLabel>
                <MDTypography variant="subtitle1" fontWeight="medium">
                  {steps[0].label}
                </MDTypography>
                <MDTypography variant="caption" color="text.secondary">
                  {steps[0].description}
                </MDTypography>
              </StepLabel>
              <StepContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <MDBox mb={2}>
                      <InputLabel sx={{ mb: 1 }}>
                        Institution Type *
                      </InputLabel>
                      <Autocomplete
                        options={dropdowns.instTypes}
                        getOptionLabel={(option) => option.txt || ""}
                        value={dropdowns.instTypes.find((o) => o.id === form.instType) || null}
                        onChange={(event, newValue) => {
                          setForm((prev) => ({ ...prev, instType: newValue?.id || "", inst: "" }));
                        }}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            placeholder="Select Institution Type (e.g., School, Engineering College, etc.)" 
                            size="small" 
                            fullWidth 
                            variant="outlined"
                            helperText="You can add school details (SSLC, HSS, CBSE, etc.) or higher education details"
                          />
                        )}
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12}>
                    <MDBox mb={2}>
                      <InputLabel sx={{ mb: 1 }}>
                        District *
                      </InputLabel>
                      <Autocomplete
                        options={dropdowns.districts}
                        getOptionLabel={(option) => option.txt || ""}
                        value={dropdowns.districts.find((o) => o.id === form.district) || null}
                        onChange={(event, newValue) => {
                          setForm((prev) => ({ ...prev, district: newValue?.id || "", inst: "" }));
                        }}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            placeholder="Select District" 
                            size="small" 
                            fullWidth 
                            variant="outlined"
                          />
                        )}
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12}>
                    <MDBox mb={2}>
                      <InputLabel sx={{ mb: 1 }}>
                        Institution *
                      </InputLabel>
                      <Autocomplete
                        options={[
                          ...dropdowns.institutions,
                          { id: "add_new", txt: "➕ Add New Institution" },
                        ]}
                        getOptionLabel={(option) => option.txt || ""}
                        value={dropdowns.institutions.find((o) => o.id === form.inst) || null}
                        onChange={(event, newValue) => {
                          if (!newValue) return;
                          if (newValue.id === "add_new") {
                            if (!form.instType || !form.district) {
                              safeToast.error("Please select Institution Type and District first.");
                              return;
                            }
                            setAddInstDialogOpen(true);
                          } else {
                            setForm((prev) => ({ ...prev, inst: newValue.id }));
                          }
                        }}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            placeholder="Select Institution" 
                            size="small" 
                            fullWidth 
                            variant="outlined"
                            disabled={!form.instType || !form.district}
                          />
                        )}
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12}>
                    <MDBox display="flex" justifyContent="flex-end" gap={1} mt={2}>
                      <MDButton variant="outlined" color="secondary" onClick={handleClose}>
                        Cancel
                      </MDButton>
                      <MDButton variant="contained" color="primary" onClick={handleNext}>
                        Next
                        <Icon sx={{ ml: 0.5 }}>arrow_forward</Icon>
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </StepContent>
            </Step>

            {/* Step 2: Academic Details */}
            <Step>
              <StepLabel>
                <MDTypography variant="subtitle1" fontWeight="medium">
                  {steps[1].label}
                </MDTypography>
                <MDTypography variant="caption" color="text.secondary">
                  {steps[1].description}
                </MDTypography>
              </StepLabel>
              <StepContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <MDBox mb={2}>
                      <InputLabel sx={{ mb: 1 }}>
                        Degree / Qualification *
                      </InputLabel>
                      <Autocomplete
                        options={dropdowns.degrees}
                        getOptionLabel={(option) => option.txt || ""}
                        value={dropdowns.degrees.find((o) => o.id === form.degree) || null}
                        onChange={(event, newValue) => {
                          setForm((prev) => ({ ...prev, degree: newValue?.id || "" }));
                        }}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            placeholder="Select Degree or Qualification (e.g., SSLC, HSC, B.E., etc.)" 
                            size="small" 
                            fullWidth 
                            variant="outlined"
                            helperText="For schools, select SSLC, HSC, CBSE, etc. For higher education, select degree"
                          />
                        )}
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12}>
                    <MDBox mb={2}>
                      <InputLabel sx={{ mb: 1 }}>
                        Course / Specialization
                      </InputLabel>
                      <TextField
                        name="txtCourse"
                        value={form.txtCourse}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder="e.g., Computer Science, Mechanical Engineering"
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12}>
                    <MDBox mb={2}>
                      <InputLabel sx={{ mb: 1 }}>
                        Medium of Instruction *
                      </InputLabel>
                      <Autocomplete
                        options={dropdowns.medium}
                        getOptionLabel={(option) => option.txt || ""}
                        value={dropdowns.medium.find((o) => o.id === form.medium) || null}
                        onChange={(event, newValue) => {
                          setForm((prev) => ({ ...prev, medium: newValue?.id || "" }));
                        }}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            placeholder="Select Medium" 
                            size="small" 
                            fullWidth 
                            variant="outlined"
                          />
                        )}
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12}>
                    <MDBox display="flex" justifyContent="space-between" gap={1} mt={2}>
                      <MDButton variant="outlined" color="secondary" onClick={handleBack}>
                        <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
                        Back
                      </MDButton>
                      <MDButton variant="contained" color="primary" onClick={handleNext}>
                        Next
                        <Icon sx={{ ml: 0.5 }}>arrow_forward</Icon>
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </StepContent>
            </Step>

            {/* Step 3: Duration & Performance */}
            <Step>
              <StepLabel>
                <MDTypography variant="subtitle1" fontWeight="medium">
                  {steps[2].label}
                </MDTypography>
                <MDTypography variant="caption" color="text.secondary">
                  {steps[2].description}
                </MDTypography>
              </StepLabel>
              <StepContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <MDBox mb={2}>
                      <InputLabel sx={{ mb: 1 }}>
                        Start Date
                      </InputLabel>
                      <TextField
                        name="txtFrom"
                        type="month"
                        value={form.txtFrom}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        helperText="Select the month and year when you started"
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <MDBox mb={2}>
                      <InputLabel sx={{ mb: 1 }}>
                        End Date
                      </InputLabel>
                      <TextField
                        name="txtTo"
                        type="month"
                        value={form.txtTo}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        helperText="Select the month and year when you completed (or leave blank if ongoing)"
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12}>
                    <MDBox mb={2}>
                      <InputLabel sx={{ mb: 1 }}>
                        Percentage / CGPA
                      </InputLabel>
                      <TextField
                        name="txtPers"
                        type="number"
                        value={form.txtPers}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder="e.g., 85.5 or 8.5 (for CGPA)"
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                        helperText="Enter your percentage (0-100) or CGPA"
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12}>
                    <MDBox display="flex" justifyContent="space-between" gap={1} mt={2}>
                      <MDButton variant="outlined" color="secondary" onClick={handleBack}>
                        <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
                        Back
                      </MDButton>
                      <MDButton 
                        variant="contained" 
                        color="primary" 
                        onClick={handleSubmit}
                        startIcon={<Icon>{editingEducation ? "save" : "check"}</Icon>}
                      >
                        {editingEducation ? "Update Education" : "Save Education"}
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </StepContent>
            </Step>
          </Stepper>
        </DialogContent>
      </Dialog>

      {/* Add Institution Dialog */}
      <Dialog 
        open={addInstDialogOpen} 
        onClose={() => setAddInstDialogOpen(false)} 
        fullWidth 
        maxWidth="xs"
        PaperProps={{ 
          sx: { 
            bgcolor: "background.paper",
          } 
        }}
      >
        <DialogTitle>Add New Institution</DialogTitle>
        <DialogContent sx={{ bgcolor: "background.paper" }}>
          <TextField
            fullWidth
            label="Full Name *"
            value={newInstitution.fullName}
            onChange={(e) => setNewInstitution({ ...newInstitution, fullName: e.target.value })}
            margin="dense"
            required
          />
          <TextField
            fullWidth
            label="Short Name"
            value={newInstitution.shortName}
            onChange={(e) => setNewInstitution({ ...newInstitution, shortName: e.target.value })}
            margin="dense"
          />
          <TextField
            fullWidth
            label="Notes"
            value={newInstitution.notes}
            onChange={(e) => setNewInstitution({ ...newInstitution, notes: e.target.value })}
            margin="dense"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: "background.paper" }}>
          <MDButton variant="outlined" color="secondary" onClick={() => setAddInstDialogOpen(false)}>
            Cancel
          </MDButton>
          <MDButton variant="contained" color="primary" onClick={handleAddInstitution}>
            Add Institution
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
};

export default EducationDetails;
